import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, IconButton} from '@mui/material';
import classNames from 'classnames';
import moment from 'moment';
import {FormattedMessage, useIntl} from 'react-intl';
import {useMediaQuery} from 'react-responsive';
import {useNavigate} from 'react-router-dom';
import {NAVIGATION_ROUTES} from '../../routes/NavigationRoute';
import {SVGS} from '../../assets/images';
import ScheduleDateSelector from '../../components/Schedule/header/components/schedule-date-selector';
import ViewSelector from '../../components/Schedule/header/components/schedule-type-selector';
import Filter from '../../components/Schedule/header/components/filter';
import {FilterState} from '../../components/Schedule/header/components/filter/types';
import TotalV2 from '../../components/Schedule/total-v2';
import TimeSheetGroupTool from '../../components/Schedule/timesheet-group-tool';
import PageHeader from '../../components/shared/page-header';
import PageFooter from '../../components/shared/page-footer';
import PageWrapperV2 from '../../components/shared/page-wrapper-v2';
import SearchInput from '../../components/shared/search-input';
import TimeTrackingPageBody from '../../components/shared/time-tracking-page-body';
import {getAllEmployees} from '../../features/employees/short';
import {getEmployee} from '../../features/employees';
import {Employee} from '../../features/employees/types';
import {setIndicatorLoading} from '../../features/indicator';
import {getProjects} from '../../features/projects/filter';
import {getAllProjectTypes} from '../../features/types';
import {
    downloadTimeReportByEmployeeDaily,
    downloadTimeReportByEmployee,
    downloadTimeReportByProjectDaily,
    downloadTimeReportByProject,
} from '../../features/report';
import {VisibleDays} from '../../features/report/types';
import {getWorkCalendar, getWorkCalendars} from '../../features/settings/workcalendar';
import {Year} from '../../features/settings/workcalendar/types';
import {updateTimeTrackingDateRangeFilter, updateTimeTrackingPropertyFilter, updateTimeTrackingDetailedByDay, updateTimeTrackingVisibleDays} from '../../features/time-tracking-filters';
import {TimeSheetGroupType} from '../../features/schedule/types';
import {useAppDispatch, useAppSelector} from '../../hooks/hooks';
import {Lx} from '../../i18n/consts';
import TimeReportGrid from './components/time-report-grid';
import {setGroupBy as setTimeReportGroupBy} from '../../features/time-report/slice';
import {ExpandedGroupsRef} from '../../components/shared/grid/types';
import {useGridViewHandoff} from '../../hooks/useGridViewHandoff';
import TimeReportMobileView from './components/time-report-mobile-view';
import TimeReportMobileFilterSheet from './components/top-bar/component/mobile-filter-sheet/index';
import TimeReportSearchProvider, {useTimeReportSearch} from './contexts/search';
import useTimeReportView from './hooks/useTimeReportView';
import { filterTimeReportItems } from './helpers/filter-time-report-items';
import {
    aggregateTimeReportItems,
    ALLOWED_RANGE_TYPES,
    DEFAULT_PROPERTY_FILTER,
    filterDetailedTimeReportItems,
    hasSelectedTimeReportEntities,
    sortTimeReportItems,
} from './helpers/time-report-page';
import styles from './time-report-page.module.scss';
import topBarStyles from './components/top-bar/time-report-top-bar.module.scss';
import { getDefaultMonthRange, getSelectedDateRangeFromFilter, mapWorkCalendarYears } from '../../components/shared/calendar/utils';
import usePersistedSummary from '../../hooks/usePersistedSummary';
import { EMPTY_SUMMARY_HOURS } from '../../helpers/empty-summary-hours';
import { getTimeReportSummaryHours } from '../../helpers/time-report-summary-hours';
import { TIMEREPORT_SUMMARY_STORAGE_KEYS } from '../../helpers/summary-storage';
import { canSeeOtherTimeReportItems, canSeeTimeReportPage, canSeeWorkCalendars } from '../../features/profile/entityPermissions';
import { REmployees } from '../../features/roles/types';
import { PIdNameModel } from '../../features/projects/types';

export default function TimeReportV2Page() {
    return (
        <TimeReportSearchProvider>
            <TimeReportPageContent />
        </TimeReportSearchProvider>
    );
}

function TimeReportPageContent() {
    const today = moment();
    const currentYear = today.year();
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [years, setYears] = useState<Array<Year>>([]);
    const [isFilterModalOpen, setFilterModalOpen] = useState(false);
    const [isDateSelectorOpen, setDateSelectorOpen] = useState(false);
    // Lifted into redux so it survives the trip to the favourites page, like detailedByDay below.
    const groupBy = useAppSelector((selector) => selector.timereport.groupBy);
    const setGroupBy = useCallback(
        (value: TimeSheetGroupType) => dispatch(setTimeReportGroupBy(value)),
        [dispatch]
    );
    // Lifted into redux so the favorites page can capture/restore these view options.
    const detailedByDay = useAppSelector((selector) => selector.timeTrackingFilters.detailedByDay);
    const visibleDays = useAppSelector((selector) => selector.timeTrackingFilters.visibleDays);
    const timeReportGridRef = useRef<ExpandedGroupsRef>(null);
    // Group keys mean different things per view and per day-detail mode, so both form the scope.
    const { restoredGroupKeys, armSnapshot } = useGridViewHandoff(
        'timereport',
        `${groupBy}-${detailedByDay}`,
        timeReportGridRef
    );
    // The favourites page returns here, so preserve the expanded groups on the way out.
    const handleFavoritesClick = useCallback(() => {
        armSnapshot();
        navigate(NAVIGATION_ROUTES.TIMEREPORT_FAVORITES);
    }, [armSnapshot, navigate]);
    const setDetailedByDay = updateTimeTrackingDetailedByDay;
    const setVisibleDays = updateTimeTrackingVisibleDays;
    const [isReportDownloading, setIsReportDownloading] = useState(false);
    const [ownEmployeeDetails, setOwnEmployeeDetails] = useState<Employee | null>(null);

    const employees = useAppSelector((selector) => selector.rEmployees);
    const projects = useAppSelector((selector) => selector.eProjects);
    const projectType = useAppSelector((selector) => selector.types.projects);
    const profile = useAppSelector((selector) => selector.profile);
    const { workCalendars } = useAppSelector((selector) => selector.workCalendar);
    const dateRangeFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter?.dateRangeFilter);
    const propertyFilter = useAppSelector((selector) => selector.timeTrackingFilters.filter?.propertyFilter ?? DEFAULT_PROPERTY_FILTER);
    const { data, loading: dataLoading, dailyEmployeeTotals } = useTimeReportView(detailedByDay, visibleDays);
    const { change: searchChange, value: search } = useTimeReportSearch();
    const isMobile = useMediaQuery({ maxWidth: 1000 });
    const isCompactDesktop = useMediaQuery({ maxWidth: 1200 });
    const canViewTimeReport = canSeeTimeReportPage(profile);
    const canViewOtherTimeReportItems = canSeeOtherTimeReportItems(profile);
    const canLoadScopedTimeReportOptions = canViewTimeReport;
    const canLoadWorkCalendars = canSeeWorkCalendars(profile);
    const ownEmployeeId = profile.id;
    const ownEmployee = useMemo<REmployees | null>(() => {
        if (!ownEmployeeId) {
            return null;
        }

        return employees.find((employee) => employee.id === ownEmployeeId)
            ?? { id: ownEmployeeId, name: profile.name, isDefault: false };
    }, [employees, ownEmployeeId, profile.name]);
    const filterEmployees = useMemo<REmployees[]>(() => (
        canViewOtherTimeReportItems
            ? employees
            : ownEmployee
                ? [ownEmployee]
                : []
    ), [canViewOtherTimeReportItems, employees, ownEmployee]);
    const { orderedData, summaryItems } = useMemo(() => {
        const filteredItems = filterTimeReportItems(data ?? [], search ?? '', !detailedByDay, detailedByDay);
        const detailedItems = detailedByDay
            ? filterDetailedTimeReportItems(
                filteredItems,
                groupBy,
                visibleDays
            )
            : filteredItems;
        const visibleItems = detailedByDay ? detailedItems : aggregateTimeReportItems(filteredItems);

        return {
            orderedData: sortTimeReportItems(visibleItems, groupBy, detailedByDay),
            summaryItems: detailedByDay ? detailedItems : filteredItems
        };
    }, [data, search, detailedByDay, groupBy, visibleDays]);

    const filterProjects = useMemo<PIdNameModel[]>(() => {
        if (projects.length > 0) {
            return projects;
        }

        if (ownEmployeeDetails?.projects?.length) {
            return ownEmployeeDetails.projects;
        }

        const uniqueProjects = new Map<number, PIdNameModel>();
        (data ?? []).forEach((item) => {
            if (!item.projectId) {
                return;
            }

            uniqueProjects.set(item.projectId, {
                id: item.projectId,
                name: item.projectName,
                isDefault: false,
            });
        });

        return Array.from(uniqueProjects.values());
    }, [data, ownEmployeeDetails?.projects, projects]);

    const controlsDisabled = false;
    const reportActionDisabled = isReportDownloading;
    const ready = !dataLoading;
    const summaryHours = usePersistedSummary({
        items: summaryItems,
        controlsDisabled,
        emptySummary: EMPTY_SUMMARY_HOURS,
        storageKeys: TIMEREPORT_SUMMARY_STORAGE_KEYS,
        calculateSummary: getTimeReportSummaryHours
    });

    const defaults = useMemo(() => getDefaultMonthRange(today), [today.valueOf()]);

    const selectedDates = useMemo(
        () => getSelectedDateRangeFromFilter(dateRangeFilter, defaults),
        [dateRangeFilter, defaults]
    );

    const calendarYears = useMemo(() => mapWorkCalendarYears(years), [years]);

    const mobileFiltersOpen = isMobile && isFilterModalOpen;
    const hideTimeReport = isMobile;

    const hasDate = useMemo(() => {
        const [s, e] = selectedDates;
        return !(s.isSame(defaults.startDate, 'day') && e.isSame(defaults.endDate, 'day'));
    }, [selectedDates, defaults]);

    const hasSelectedEntities = hasSelectedTimeReportEntities(propertyFilter);

    const hasSearch = (search?.trim()?.length ?? 0) > 0;
    const hasAnyFilters = hasSearch || hasDate || hasSelectedEntities || detailedByDay;

    const handleDownloadReport = useCallback(async () => {
        setIsReportDownloading(true);

        const dateFrom = dateRangeFilter?.startDate ?? defaults.startDate.format('YYYY-MM-DD');
        const dateTo = dateRangeFilter?.endDate ?? defaults.endDate.format('YYYY-MM-DD');
        const employeeIds = canViewOtherTimeReportItems
            ? propertyFilter.selectedEmployees?.map((x) => x.originalId as number) ?? []
            : ownEmployeeId
                ? [ownEmployeeId]
                : [];
        const projectIds = propertyFilter.selectedProjects?.map((x) => x.originalId as number) ?? [];

        try {
            if (detailedByDay) {
                if (groupBy === TimeSheetGroupType.PROJECTVIEW) {
                    await dispatch(downloadTimeReportByProjectDaily({
                        dateFrom,
                        dateTo,
                        employeeIds,
                        projectIds,
                        showAbsences: propertyFilter.showAbsences ?? true,
                        header: 'time-report-by-project-daily',
                        visibleDays,
                        searchQuery: search,
                    })).unwrap();
                    return;
                }

                await dispatch(downloadTimeReportByEmployeeDaily({
                    dateFrom,
                    dateTo,
                    employeeIds,
                    projectIds,
                    showAbsences: propertyFilter.showAbsences ?? true,
                    header: 'time-report-by-employee-daily',
                    visibleDays,
                    searchQuery: search,
                })).unwrap();
                return;
            }

            if (groupBy === TimeSheetGroupType.PROJECTVIEW) {
                await dispatch(downloadTimeReportByProject({
                    dateFrom,
                    dateTo,
                    employeeIds,
                    projectIds,
                    showAbsences: propertyFilter.showAbsences ?? true,
                    header: 'time-report-by-project',
                    searchQuery: search,
                })).unwrap();
                return;
            }

            await dispatch(downloadTimeReportByEmployee({
                dateFrom,
                dateTo,
                employeeIds,
                projectIds,
                showAbsences: propertyFilter.showAbsences ?? true,
                header: 'time-report-by-employee',
                searchQuery: search,
            })).unwrap();
        } finally {
            setIsReportDownloading(false);
        }
    }, [
        dateRangeFilter?.endDate,
        dateRangeFilter?.startDate,
        defaults,
        detailedByDay,
        dispatch,
        groupBy,
        canViewOtherTimeReportItems,
        ownEmployeeId,
        propertyFilter.selectedEmployees,
        propertyFilter.selectedProjects,
        propertyFilter.showAbsences,
        search,
        visibleDays,
    ]);

    const handleDateRangeChange = useCallback((values: moment.Moment[]) => new Promise<void>((resolve) => {
        const nextRange = {
            startDate: values[0].format('YYYY-MM-DD'),
            endDate: values[1].format('YYYY-MM-DD'),
        };
        updateTimeTrackingDateRangeFilter(nextRange);
        resolve();
    }), []);

    const handlePropertyFilterChanged = useCallback((value: FilterState) => new Promise<void>((resolve) => {
        const payload: FilterState = {
            selectedProjects: value.selectedProjects ?? [],
            selectedProjectTypes: value.selectedProjectTypes ?? [],
            selectedEmployees: value.selectedEmployees ?? [],
            selectedShiftTypes: value.selectedShiftTypes ?? [],
            selectedAbsenceTypes: value.selectedAbsenceTypes ?? [],
            selectedStatuses: value.selectedStatuses ?? [],
            showAbsences: typeof value.showAbsences === 'boolean' ? value.showAbsences : true
        };
        updateTimeTrackingPropertyFilter(payload);
        resolve();
    }), []);

    const handleResetFilters = useCallback(() => {
        const resetPayload: FilterState = {
            selectedProjects: [], selectedProjectTypes: [], selectedEmployees: [],
            selectedShiftTypes: [], selectedAbsenceTypes: [], selectedStatuses: [], showAbsences: true
        };
        updateTimeTrackingPropertyFilter(resetPayload);
        setDetailedByDay(false);
        setVisibleDays(VisibleDays.All);
        searchChange('');
        handleDateRangeChange([defaults.startDate, defaults.endDate]);
    }, [defaults.endDate, defaults.startDate, handleDateRangeChange, searchChange]);

    const handleDateSelectorOpenChange = useCallback((open: boolean) => {
        setDateSelectorOpen(open);
        if (open) {
            setFilterModalOpen(false);
        }
    }, []);

    const dateSelectorState = useMemo(() => ({
        open: isDateSelectorOpen,
        setOpen: handleDateSelectorOpenChange
    }), [isDateSelectorOpen, handleDateSelectorOpenChange]);

    const propertyFilters = useMemo<FilterState>(() => ({
        selectedEmployees: canViewOtherTimeReportItems ? propertyFilter.selectedEmployees ?? [] : [],
        selectedProjects: propertyFilter.selectedProjects ?? [],
        selectedProjectTypes: propertyFilter.selectedProjectTypes ?? [],
        selectedShiftTypes: propertyFilter.selectedShiftTypes ?? [],
        selectedAbsenceTypes: propertyFilter.selectedAbsenceTypes ?? [],
        selectedStatuses: propertyFilter.selectedStatuses ?? [],
        showAbsences: propertyFilter.showAbsences ?? true,
    }), [canViewOtherTimeReportItems, propertyFilter]);

    const shouldLockScroll = isMobile && (mobileFiltersOpen || isDateSelectorOpen);
    const gridClasses = classNames(styles.gridSection, { [styles.mobileHidden]: hideTimeReport });
    const pageBodyClasses = classNames(styles.pageBodyContent, {
        [styles.scrollLocked]: shouldLockScroll
    });
    const shouldShowCreateFooterButton = isMobile && !mobileFiltersOpen;
    const pageBodyWrapperClasses = classNames(styles.pageBodyWrapper, {
        [styles.pageBodyWrapperWithFooter]: shouldShowCreateFooterButton
    });

    const createReportLabel = intl.formatMessage({ id: Lx.Schedule.CREATE_REPORT, defaultMessage: 'Create report' });
    const creatingReportLabel = intl.formatMessage({ id: Lx.Schedule.CREATING_REPORT, defaultMessage: 'Creating...' });

    const loadCalendarYears = useCallback(async (calendarId: number) => {
        const loadedYears = await Promise.all(
            [currentYear - 1, currentYear].map((year) =>
                dispatch(getWorkCalendar({ workCalendarId: calendarId, year })).unwrap()
            )
        );

        setYears((currentYears) => {
            const nextYears = [...currentYears];

            loadedYears.forEach((loadedYear) => {
                if (!nextYears.find((year) => year.year === loadedYear.year)) {
                    nextYears.push(loadedYear);
                }
            });

            return nextYears;
        });
    }, [currentYear, dispatch]);

    useEffect(() => {
        const initializeTimeReportPage = async () => {
            if (!canViewOtherTimeReportItems && profile.id && ownEmployeeDetails?.id !== profile.id) {
                dispatch(getEmployee(profile.id)).unwrap().then(setOwnEmployeeDetails).catch(console.error);
            }

            if (canLoadScopedTimeReportOptions && employees.length < 1) {
                dispatch(getAllEmployees());
            }

            if (canLoadScopedTimeReportOptions && projects.length < 1) {
                dispatch(getProjects());
            }

            if (canLoadScopedTimeReportOptions && projectType.total < 1) {
                dispatch(getAllProjectTypes());
            }

            if (!canLoadWorkCalendars) {
                return;
            }
            const primaryCalendar = workCalendars[0] ?? (await dispatch(getWorkCalendars()).unwrap())[0];
            if (primaryCalendar) {
                await loadCalendarYears(primaryCalendar.id);
            }
        };

        void initializeTimeReportPage();
    }, [
        canLoadScopedTimeReportOptions,
        canLoadWorkCalendars,
        canViewOtherTimeReportItems,
        dispatch,
        employees.length,
        loadCalendarYears,
        ownEmployeeDetails?.id,
        profile.id,
        projects.length,
        projectType.total,
        workCalendars,
    ]);

    useEffect(() => {
        const allowedEmployeeIds = new Set(filterEmployees.map((employee) => employee.id));
        const allowedProjectIds = new Set(filterProjects.map((project) => project.id));
        const selectedEmployees = propertyFilter.selectedEmployees ?? [];
        const selectedProjects = propertyFilter.selectedProjects ?? [];
        const nextEmployees = canViewOtherTimeReportItems
            ? selectedEmployees
            : selectedEmployees.filter((employee) => allowedEmployeeIds.has(employee.originalId as number));
        const nextProjects = allowedProjectIds.size < 1
            ? selectedProjects
            : selectedProjects.filter((project) => allowedProjectIds.has(project.originalId as number));

        if (nextEmployees.length === selectedEmployees.length && nextProjects.length === selectedProjects.length) {
            return;
        }

        updateTimeTrackingPropertyFilter({
            ...propertyFilter,
            selectedEmployees: nextEmployees,
            selectedProjects: nextProjects,
        });
    }, [canViewOtherTimeReportItems, filterEmployees, filterProjects, propertyFilter]);

    useEffect(() => {
        setIndicatorLoading(dataLoading || isReportDownloading);
        return () => {
            setIndicatorLoading(false);
        };
    }, [dataLoading, isReportDownloading]);

    useEffect(() => {
        if (!dateRangeFilter?.startDate || !dateRangeFilter?.endDate) {
            handleDateRangeChange([defaults.startDate, defaults.endDate]);
        }
    }, [dateRangeFilter, defaults.endDate, defaults.startDate, handleDateRangeChange]);

    useEffect(() => {
        if (!isMobile) {
            setFilterModalOpen(false);
            setDateSelectorOpen(false);
        }
    }, [isMobile]);

    useEffect(() => {
        if (!shouldLockScroll || typeof document === 'undefined') {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [shouldLockScroll]);

    useEffect(() => {
        if (!isMobile || typeof window === 'undefined') {
            return;
        }

        if (isFilterModalOpen) {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, [isMobile, isFilterModalOpen]);

    const header = isMobile ? (
        <div className={classNames(topBarStyles.container, topBarStyles.mobileContainer)}>
            <div className={topBarStyles.mobileHeaderRow}>
                <div className={topBarStyles.titleRow}>
                    <div className={topBarStyles.titleWithSpinner}>
                        <h3>
                            <FormattedMessage id={'timereport'} />
                            :
                        </h3>
                    </div>
                    <div className={topBarStyles.titleViewSelector}>
                        <ViewSelector
                            value={groupBy}
                            onChange={(value) => setGroupBy(value ?? TimeSheetGroupType.EMPLOYEEVIEW)}
                            disabled={controlsDisabled}
                        />
                    </div>
                </div>
                <div className={topBarStyles.mobileHeaderActions}>
                    <IconButton
                        className={classNames(
                            topBarStyles.mobileIconButton,
                            {
                                [topBarStyles.mobileIconButtonActive]: hasAnyFilters || isFilterModalOpen,
                                [topBarStyles.mobileIconButtonOpen]: isFilterModalOpen,
                            }
                        )}
                        size={'small'}
                        onClick={() => {
                            if (controlsDisabled) {
                                return;
                            }
                            setFilterModalOpen((value) => {
                                const next = !value;
                                if (next) {
                                    setDateSelectorOpen(false);
                                }
                                return next;
                            });
                        }}
                        disabled={controlsDisabled}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                    >
                        <SVGS.FilterAltRounded />
                    </IconButton>
                    <IconButton
                        className={topBarStyles.mobileIconButton}
                        size={'small'}
                        aria-label='favorite-filters'
                        onClick={handleFavoritesClick}
                        disabled={controlsDisabled}
                        disableRipple
                        disableFocusRipple
                        disableTouchRipple
                    >
                        <SVGS.StarIcon />
                    </IconButton>
                </div>
            </div>
        </div>
    ) : (
        <div className={topBarStyles.container}>
            <Box className={topBarStyles.header}>
                <div className={classNames({
                    [topBarStyles.headerItem]: true,
                    [topBarStyles.dateSelector]: true
                })}>
                    <div className={topBarStyles.titleRow}>
                        <div className={topBarStyles.titleWithSpinner}>
                            <h3>
                                <FormattedMessage id={'timereport'} />
                                :
                            </h3>
                        </div>
                        <div className={topBarStyles.titleViewSelector}>
                            <ViewSelector
                                value={groupBy}
                                onChange={(value) => setGroupBy(value ?? TimeSheetGroupType.EMPLOYEEVIEW)}
                                disabled={controlsDisabled}
                            />
                        </div>
                    </div>
                </div>
                <Box className={classNames(topBarStyles.dateSelector, topBarStyles.headerCalendar)}>
                    <ScheduleDateSelector
                        selectedDates={selectedDates}
                        onRangeSelected={handleDateRangeChange}
                        years={calendarYears}
                        minDate={today.clone().add(-365, 'days')}
                        maxDate={today.clone().endOf('month')}
                        allowedRangeTypes={ALLOWED_RANGE_TYPES}
                        calendarState={isMobile ? dateSelectorState : undefined}
                        disabled={controlsDisabled}
                    />
                </Box>

                <Box className={topBarStyles.actions}>
                    <Button className={topBarStyles.actionButton} variant='loadMore' onClick={handleDownloadReport} disabled={reportActionDisabled}>
                        <span className={topBarStyles.reportButtonIconSlot}>
                            <SVGS.AddChartIcon className={topBarStyles.reportIcon}/>
                        </span>
                        {!isCompactDesktop && (
                            <span className={topBarStyles.reportButtonLabel}>
                                <span className={topBarStyles.reportButtonLabelMeasure}>{createReportLabel}</span>
                                <span className={topBarStyles.reportButtonLabelText}>
                                    {isReportDownloading ? creatingReportLabel : createReportLabel}
                                </span>
                            </span>
                        )}
                    </Button>
                    <Button
                        aria-label='favorite-filters'
                        title={intl.formatMessage({ id: Lx.General.FAVORITE_FILTERS })}
                        className={topBarStyles.actionButton}
                        variant='loadMore'
                        onClick={handleFavoritesClick}
                        disabled={controlsDisabled}
                    >
                        <span className={topBarStyles.reportButtonIconSlot}>
                            <SVGS.StarIcon className={topBarStyles.reportIcon}/>
                        </span>
                    </Button>
                </Box>
            </Box>
        </div>
    );

    const body = isMobile ? (
        <>
            <TimeReportMobileFilterSheet
                open={mobileFiltersOpen}
                onClose={() => setFilterModalOpen(false)}
                onApply={handlePropertyFilterChanged}
                onClear={handleResetFilters}
                search={search ?? ''}
                onSearchChange={searchChange}
                filters={propertyFilters}
                employees={filterEmployees}
                projects={filterProjects}
                projectTypes={projectType.items}
                disabled={controlsDisabled}
                detailedByDay={detailedByDay}
                onDetailedByDayChange={setDetailedByDay}
                visibleDays={visibleDays}
                onVisibleDaysChange={setVisibleDays}
                disableEmployeesFilter={!canViewOtherTimeReportItems}
            />
            {!mobileFiltersOpen && (
                <>
                    <div className={topBarStyles.mobileDateRow}>
                        <div className={topBarStyles.mobileDateSelector}>
                            <ScheduleDateSelector
                                selectedDates={selectedDates}
                                onRangeSelected={handleDateRangeChange}
                                years={calendarYears}
                                minDate={today.clone().add(-365, 'days')}
                                maxDate={today.clone().endOf('month')}
                                allowedRangeTypes={ALLOWED_RANGE_TYPES}
                                calendarState={isMobile ? dateSelectorState : undefined}
                                disabled={controlsDisabled}
                            />
                        </div>
                    </div>
                    <div className={topBarStyles.mobileSummary}>
                        <>
                            <TotalV2
                                totalHours={summaryHours.totalHours}
                                approved={summaryHours.approved}
                                billed={summaryHours.billed}
                                absenceHours={summaryHours.absenceHours}
                                workHours={summaryHours.workHours}
                                hidePlanned
                                hideTrackingHours={summaryHours.workHours === undefined || summaryHours.workHours < 1}
                            />
                            <TimeSheetGroupTool
                                intl={intl}
                                totalHours={summaryHours.totalHours}
                                approved={0}
                                billed={0}
                                approvable={0}
                                billable={0}
                                hideButtons
                            />
                        </>
                    </div>
                    <div className={styles.mobileReportList}>
                        { ready ? (
                            <TimeReportMobileView
                                data={orderedData ?? []}
                                intl={intl}
                                groupBy={groupBy}
                                detailedByDay={detailedByDay}
                                employeeTotals={dailyEmployeeTotals} />
                        ) : null }
                    </div>
                </>
            )}
            {!hideTimeReport && (
                <div className={gridClasses}>
                    <TimeReportGrid
                        ref={timeReportGridRef}
                        initialExpandedGroupKeys={restoredGroupKeys}
                        data={orderedData ?? []}
                        intl={intl}
                        groupBy={groupBy}
                        detailedByDay={detailedByDay}
                        employeeTotals={dailyEmployeeTotals} />
                </div>
            )}
        </>
    ) : (
        <>
            <div className={topBarStyles.container}>
                <Box className={topBarStyles.subheader}>
                    <div className={topBarStyles.searchFilter}>
                        <SearchInput
                            searchValue={search ?? ''}
                            onChange={searchChange}
                            width='260px'
                            minWidth='82px'
                            disabled={controlsDisabled}
                        />
                        <div className={topBarStyles.filterActions}>
                            <Filter
                                hasSelected={hasSelectedEntities || detailedByDay}
                                onChange={handlePropertyFilterChanged}
                                disableStatusFilter={true}
                                disableAbsenceAndShiftTypesFilter={true}
                                showAbsencesToggle={true}
                                disabled={controlsDisabled}
                                compactNarrowDesktop
                                detailedByDay={detailedByDay}
                                onDetailedByDayChange={setDetailedByDay}
                                visibleDays={visibleDays}
                                onVisibleDaysChange={setVisibleDays}
                                employees={filterEmployees}
                                projects={filterProjects}
                                projectTypes={projectType.items}
                                disableEmployeesFilter={!canViewOtherTimeReportItems}
                            />

                            { hasAnyFilters && (
                                <Button
                                    className={topBarStyles.resetButton}
                                    onClick={handleResetFilters}
                                    disabled={controlsDisabled}>
                                    <SVGS.CancelIcon className={topBarStyles.resetIcon}/>
                                </Button>
                            )}
                        </div>
                    </div>
                    <div className={topBarStyles.info}>
                        { summaryHours && (
                            <>
                                <TotalV2 totalHours={summaryHours.totalHours}
                                    approved={summaryHours.approved}
                                    billed={summaryHours.billed}
                                    absenceHours={summaryHours.absenceHours}
                                    workHours={summaryHours.workHours}
                                    hidePlanned={true}
                                    hideTrackingHours={summaryHours.workHours === undefined || summaryHours.workHours < 1}/>
                                <TimeSheetGroupTool
                                    intl={intl}
                                    totalHours={summaryHours.totalHours}
                                    approved={0}
                                    billed={0}
                                    approvable={0}
                                    billable={0}
                                    hideButtons
                                />
                            </>
                        )}
                    </div>
                </Box>
            </div>
            <div className={gridClasses}>
                <TimeReportGrid
                    ref={timeReportGridRef}
                    initialExpandedGroupKeys={restoredGroupKeys}
                    data={orderedData ?? []}
                    intl={intl}
                    groupBy={groupBy}
                    detailedByDay={detailedByDay}
                    employeeTotals={dailyEmployeeTotals} />
            </div>
        </>
    );

    if (!canViewTimeReport) {
        return null;
    }

    return <PageWrapperV2 className={styles.pageScope} hasMobileFooter={shouldShowCreateFooterButton}>
        <PageHeader>
            {header}
        </PageHeader>
        <TimeTrackingPageBody justifyContent={'flex-start'} alignItems={'stretch'}>
            <div className={pageBodyWrapperClasses}>
                <div className={pageBodyClasses}>
                    {body}
                </div>
            </div>
        </TimeTrackingPageBody>
        {shouldShowCreateFooterButton && (
            <PageFooter>
                <div className={styles.mobileFooter}>
                    <button
                        type='button'
                        className={classNames(styles.mobileFooterButton, {
                            [styles.mobileFooterButtonLoading]: isReportDownloading
                        })}
                        onClick={handleDownloadReport}
                        disabled={reportActionDisabled}
                    >
                        <span className={styles.mobileFooterIconSlot}>
                            <SVGS.AddChartIcon className={styles.mobileFooterIcon} />
                        </span>
                        <span className={styles.mobileFooterButtonLabel}>
                            <span className={styles.mobileFooterButtonLabelMeasure}>{createReportLabel}</span>
                            <span className={styles.mobileFooterButtonLabelText}>
                                {isReportDownloading ? creatingReportLabel : createReportLabel}
                            </span>
                        </span>
                    </button>
                </div>
            </PageFooter>
        )}
    </PageWrapperV2>
}
